/**
 * Password policy — shared rules for every screen that sets or changes a password.
 *
 * Frontend mirror of `server/services/auth/password.helper.ts`. The two bundles cannot share a
 * module, so keep them in step: the server rejects a non-compliant password on the API, the
 * frontend reports the same rule before the request is sent. A screen that drifts from these
 * values shows a rule the API does not enforce, or refuses a password the API would accept.
 */

/** Minimum length every newly set or changed password must meet. */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Character-class rule for a new password: at least one lowercase letter, one uppercase letter,
 * one digit and one of `@ $ ! % * ? & #`. Length is deliberately not part of this pattern — check
 * it against PASSWORD_MIN_LENGTH so a too-short password can be reported separately from one
 * missing a character class.
 */
export const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/;

/**
 * Checks a password against the full policy — both the minimum length and the character-class rule.
 * @param value - the plaintext password entered on the form.
 * @returns true when the password satisfies every rule.
 */
export const isPasswordCompliant = (value: unknown): boolean => {
  if (typeof value !== 'string' || value.length < PASSWORD_MIN_LENGTH) {
    return false;
  }
  return PASSWORD_PATTERN.test(value);
};
